import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from './useKittingApi';

// ─── Constants ────────────────────────────────────────────────────────────────
const STEPS = ['upload', 'configure', 'preview', 'execute'];
const STEP_LABELS = { upload: 'Upload File', configure: 'Configure Kits', preview: 'Review Matches', execute: 'Import' };
const CONFLICT_ACTIONS = [
  { value: 'create', label: 'Create new fitment' },
  { value: 'overwrite', label: 'Overwrite existing' },
  { value: 'skip', label: 'Skip (ignore)' },
  { value: 'create_additional', label: 'Create additional row' }
];

// OEM numbers that mean "nothing real is assigned here yet". A fitment sitting at the
// kit's target position holding one of these is a placeholder waiting for the kit, so it
// should default to Overwrite instead of being silently skipped as a conflict.
const PLACEHOLDER_OEM_NUMBERS = new Set(['tba', 'none', 'n/a', 'na', 'review', 'reviewoem', 'unassigned']);
const isPlaceholderOemNumber = (oem) => {
  const value = (oem ?? '').toString().trim().toLowerCase();
  return value === '' || PLACEHOLDER_OEM_NUMBERS.has(value);
};

// Build the per-model conflict actions for a preview result.
//  - already linked to the kit we're using  → skip (nothing to do)
//  - placeholder OEM (TBA/None/blank) free  → overwrite (this is the row the kit belongs on)
//  - anything else                          → leave on the group default
const buildConflictOverrides = (result, selectedKitId) => {
  const overrides = {};
  const rowsByModel = new Map();
  (result?.conflicts || []).forEach(c => {
    const key = String(c.ModelKey);
    if (!rowsByModel.has(key)) rowsByModel.set(key, []);
    rowsByModel.get(key).push(c);
  });
  rowsByModel.forEach((rows, modelKey) => {
    const alreadyLinked = !!selectedKitId && rows.some(c =>
      c.LinkedKitId === selectedKitId || (c.OEMNumber && c.OEMNumber === selectedKitId)
    );
    if (alreadyLinked) {
      overrides[modelKey] = 'skip';
      return;
    }
    const hasFreePlaceholder = rows.some(c => !c.LinkedKitId && isPlaceholderOemNumber(c.OEMNumber));
    if (hasFreePlaceholder) overrides[modelKey] = 'overwrite';
  });
  return overrides;
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const makeStyles = (isDark) => ({
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1200
  },
  modal: {
    background: isDark ? '#1e1e1e' : '#fff', color: isDark ? '#e5e5e5' : '#222',
    borderRadius: '12px', width: 'min(1100px, 95vw)', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    boxShadow: '0 12px 40px rgba(0,0,0,0.3)'
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '1rem 1.25rem', borderBottom: `1px solid ${isDark ? '#333' : '#e2e6ea'}`
  },
  body: { flex: 1, overflow: 'auto', padding: '1.25rem' },
  footer: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0.75rem 1.25rem', borderTop: `1px solid ${isDark ? '#333' : '#e2e6ea'}`, gap: '0.5rem'
  },
  card: {
    border: `1px solid ${isDark ? '#444' : '#ddd'}`, borderRadius: '8px',
    padding: '1rem', marginBottom: '1rem', background: isDark ? '#252525' : '#fafbfc'
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: {
    textAlign: 'left', padding: '0.4rem 0.6rem',
    background: isDark ? '#2a2a2a' : '#f0f2f5', fontWeight: 600, whiteSpace: 'nowrap'
  },
  td: {
    padding: '0.4rem 0.6rem', borderBottom: `1px solid ${isDark ? '#333' : '#eee'}`
  },
  btn: {
    padding: '0.45rem 0.85rem', borderRadius: '5px', border: 'none', cursor: 'pointer',
    fontWeight: 500, fontSize: '0.85rem'
  },
  btnPrimary: {
    background: '#2563eb', color: '#fff'
  },
  btnSecondary: {
    background: isDark ? '#374151' : '#e5e7eb', color: isDark ? '#e5e5e5' : '#374151'
  },
  btnDanger: {
    background: '#dc2626', color: '#fff'
  },
  badge: (color) => ({
    display: 'inline-block', padding: '0.15rem 0.45rem', borderRadius: '4px',
    fontSize: '0.75rem', fontWeight: 600, background: color, color: '#fff', marginRight: '0.3rem'
  }),
  select: {
    padding: '0.35rem 0.5rem', borderRadius: '4px', fontSize: '0.85rem',
    border: `1px solid ${isDark ? '#555' : '#ccc'}`, background: isDark ? '#333' : '#fff',
    color: isDark ? '#e5e5e5' : '#222'
  },
  input: {
    padding: '0.35rem 0.5rem', borderRadius: '4px', fontSize: '0.85rem',
    border: `1px solid ${isDark ? '#555' : '#ccc'}`, background: isDark ? '#333' : '#fff',
    color: isDark ? '#e5e5e5' : '#222'
  },
  label: { display: 'block', marginBottom: '0.35rem', fontWeight: 500, fontSize: '0.85rem' },
  error: { color: '#ef4444', fontSize: '0.85rem', marginTop: '0.3rem' },
  warning: { color: '#f59e0b', fontSize: '0.85rem', marginTop: '0.3rem' },
  success: { color: '#22c55e', fontSize: '0.85rem', marginTop: '0.3rem' },
  stepper: {
    display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.8rem', flexWrap: 'wrap'
  },
  stepDot: (active, done) => ({
    width: '28px', height: '28px', borderRadius: '50%', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '0.75rem',
    background: active ? '#2563eb' : done ? '#22c55e' : (isDark ? '#444' : '#d1d5db'),
    color: active || done ? '#fff' : (isDark ? '#aaa' : '#666')
  })
});

// ─── Auto-description builder ─────────────────────────────────────────────────
/**
 * Build a default kit description from target fitment info + models grouped by
 * Make → ModelName StreetName with year ranges.
 *
 * Format:
 *   ItemType / FitmentLocation / AdditionalInfo — Make: ModelName StreetName YYYY-YYYY,
 *   ModelName StreetName YYYY; OtherMake: ModelName StreetName YYYY-YYYY
 */
function buildDefaultKitDescription(config, matchingModels, crossMakeModels, coreItemTypes, fitmentLocMap) {
  // ── Part 1: target fitment label
  const parts = [];
  const itemType = coreItemTypes.find(t => t.ItemTypeKey === config.targetItemTypeKey);
  if (itemType?.ItemType) parts.push(itemType.ItemType);
  const locs = fitmentLocMap[config.targetItemTypeKey] || [];
  const loc = locs.find(l => l.FitmentLocationKey === config.targetFitmentLocationKey);
  if (loc?.FitmentLocation) parts.push(loc.FitmentLocation);
  if (config.targetAdditionalFitmentInfo) parts.push(config.targetAdditionalFitmentInfo);
  const fitmentLabel = parts.join(' / ');

  // ── Part 2: combine all models, group by Make → ModelName+StreetName → years
  const allModels = [...(matchingModels || []), ...(crossMakeModels || [])];
  if (!allModels.length) return fitmentLabel || '';

  // Collapse years into ranges (e.g. [1991,1992,1993,1994] → "1991-1994")
  const formatYears = (years) => {
    if (!years.length) return '';
    years.sort((a, b) => a - b);
    const ranges = [];
    let start = years[0], end = years[0];
    for (let i = 1; i < years.length; i++) {
      if (years[i] === end + 1) { end = years[i]; }
      else { ranges.push(start === end ? String(start) : `${start}-${end}`); start = end = years[i]; }
    }
    ranges.push(start === end ? String(start) : `${start}-${end}`);
    return ranges.join(', ');
  };

  // Build nested map: Make → { displayName → { years[], minSort } }
  const makeMap = {};
  for (const m of allModels) {
    const make = m.Make || 'Unknown';
    if (!makeMap[make]) makeMap[make] = {};

    // Always show ModelName + StreetName (with space) when both exist
    const mn = (m.ModelName || '').trim();
    const sn = (m.StreetName || '').trim();
    let displayName;
    if (mn && sn && mn !== sn) {
      displayName = `${mn} ${sn}`;
    } else {
      displayName = mn || sn || 'Unknown';
    }

    if (!makeMap[make][displayName]) makeMap[make][displayName] = { years: [], minSort: Infinity };
    const entry = makeMap[make][displayName];
    const yr = m.Year != null ? Number(m.Year) : null;
    if (yr && !entry.years.includes(yr)) entry.years.push(yr);
    // Keep the lowest SortOrder from any row in this model group
    const so = m.SortOrder != null ? Number(m.SortOrder) : Infinity;
    if (so < entry.minSort) entry.minSort = so;
  }

  // Sort makes alphabetically, models by first SortOrder (then name as tiebreaker)
  const sortedMakes = Object.keys(makeMap).sort((a, b) => a.localeCompare(b));
  const makeSections = sortedMakes.map(make => {
    const modelEntries = Object.entries(makeMap[make])
      .sort(([aName, aData], [bName, bData]) => (aData.minSort - bData.minSort) || aName.localeCompare(bName));
    const modelStrings = modelEntries.map(([name, data]) => {
      const yr = formatYears(data.years);
      return yr ? `${name} ${yr}` : name;
    });
    return `${make}: ${modelStrings.join(', ')}`;
  });
  const modelsLabel = makeSections.join('; ');

  if (fitmentLabel && modelsLabel) return `${fitmentLabel} — ${modelsLabel}`;
  return fitmentLabel || modelsLabel || '';
}

// ─── Searchable Select (typeahead with wildcard filtering) ────────────────────
function SearchableSelect({ options, value, onChange, placeholder, disabled, labelKey, valueKey, isDark, style: outerStyle }) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Wildcard filter: each word becomes .*word.* with .* between them
  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const words = search.trim().toLowerCase().split(/\s+/);
    const pattern = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('.*');
    try {
      const re = new RegExp(pattern, 'i');
      return options.filter(o => re.test(o[labelKey]));
    } catch {
      return options;
    }
  }, [options, search, labelKey]);

  const selectedLabel = options.find(o => o[valueKey] === value)?.[labelKey] || '';

  return (
    <div ref={wrapRef} style={{ position: 'relative', ...outerStyle }}>
      <input
        ref={inputRef}
        style={{
          padding: '0.35rem 0.5rem', borderRadius: '4px', fontSize: '0.85rem',
          border: `1px solid ${isDark ? '#555' : '#ccc'}`, background: isDark ? '#333' : '#fff',
          color: isDark ? '#e5e5e5' : '#222', width: '100%', boxSizing: 'border-box'
        }}
        placeholder={placeholder || '— Type to search —'}
        value={isOpen ? search : selectedLabel}
        disabled={disabled}
        onFocus={() => { setIsOpen(true); setSearch(''); }}
        onChange={e => { setSearch(e.target.value); if (!isOpen) setIsOpen(true); }}
        onKeyDown={e => {
          if (e.key === 'Escape') { setIsOpen(false); inputRef.current?.blur(); }
          if (e.key === 'Enter' && filtered.length === 1) {
            onChange(filtered[0][valueKey]);
            setIsOpen(false);
            inputRef.current?.blur();
          }
        }}
      />
      {value && !isOpen && (
        <span
          style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', fontSize: '0.8rem', color: isDark ? '#888' : '#999' }}
          onClick={() => { onChange(null); setSearch(''); }}
        >
          ✕
        </span>
      )}
      {isOpen && !disabled && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          maxHeight: '200px', overflow: 'auto',
          background: isDark ? '#2a2a2a' : '#fff', border: `1px solid ${isDark ? '#555' : '#ccc'}`,
          borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem', color: isDark ? '#888' : '#999' }}>No matches</div>
          ) : filtered.map(o => (
            <div
              key={o[valueKey]}
              style={{
                padding: '0.35rem 0.6rem', cursor: 'pointer', fontSize: '0.85rem',
                background: o[valueKey] === value ? (isDark ? '#374151' : '#e0e7ff') : 'transparent'
              }}
              onMouseDown={e => e.preventDefault()}
              onClick={() => { onChange(o[valueKey]); setIsOpen(false); setSearch(''); inputRef.current?.blur(); }}
              onMouseEnter={e => e.currentTarget.style.background = isDark ? '#374151' : '#f0f2f5'}
              onMouseLeave={e => e.currentTarget.style.background = o[valueKey] === value ? (isDark ? '#374151' : '#e0e7ff') : 'transparent'}
            >
              {o[labelKey]}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function KitImporter({ open, onClose, isDark = false, onImportComplete, refreshKitIds = null }) {
  const s = useMemo(() => makeStyles(isDark), [isDark]);
  const fileInputRef = useRef(null);
  const bodyRef = useRef(null);
  const userEditedDescription = useRef({}); // groupId → bool — tracks if user typed a custom description
  const isRefreshMode = !!refreshKitIds?.length;

  // ── Wizard state
  const [step, setStep] = useState('upload');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // ── Upload step
  const [fileName, setFileName] = useState('');
  const [parseResult, setParseResult] = useState(null); // { kitGroups, errors, warnings }

  // ── Configure step
  const [groupConfigs, setGroupConfigs] = useState({}); // groupId → { kitDescription, targetItemTypeKey, targetFitmentLocationKey, targetAdditionalFitmentInfo, useExistingKitId, skuGroupKey, skuResolved }
  const [coreItemTypes, setCoreItemTypes] = useState([]);
  const [fitmentLocMap, setFitmentLocMap] = useState({}); // itemTypeKey → [{ FitmentLocationKey, FitmentLocation }]
  const [skuBusy, setSkuBusy] = useState({}); // groupId → bool
  const [customGroupName, setCustomGroupName] = useState({}); // groupId → string

  // ── Preview step
  const [previewResult, setPreviewResult] = useState(null); // { results: [...] }
  const [groupActions, setGroupActions] = useState({}); // groupId → { defaultAction, modelOverrides, includeCrossMakeKeys, excludeModelKeys }
  const [expandedExtraKits, setExpandedExtraKits] = useState({}); // "groupId-kitId" → bool
  const [kitOemOverwrite, setKitOemOverwrite] = useState({}); // groupId → bool (whether to include fitments linked to other kits)
  const [noChangeSectionOpen, setNoChangeSectionOpen] = useState(false);

  // ── Execute step
  const [executeResult, setExecuteResult] = useState(null);

  // ── Load item types on mount
  useEffect(() => {
    if (!open) return;
    api.listCoreItemTypes().then(setCoreItemTypes).catch(() => setCoreItemTypes([]));
  }, [open]);

  // ── Update config for a group
  const updateGroupConfig = useCallback((groupId, updates) => {
    setGroupConfigs(prev => ({
      ...prev,
      [groupId]: { ...prev[groupId], ...updates }
    }));
  }, []);

  // ── Reset on open
  useEffect(() => {
    if (open) {
      setStep(isRefreshMode ? 'preview' : 'upload');
      setError('');
      setFileName('');
      setParseResult(null);
      setGroupConfigs({});
      setPreviewResult(null);
      setGroupActions({});
      setExecuteResult(null);
      setSkuBusy({});
      setCustomGroupName({});
      setExpandedExtraKits({});
      setKitOemOverwrite({});
      setNoChangeSectionOpen(false);
      userEditedDescription.current = {};
      if (bodyRef.current) bodyRef.current.scrollTop = 0;

      // If refresh mode, call refresh-preview API immediately
      if (isRefreshMode) {
        setBusy(true);
        setError('');
        api.refreshPreview(refreshKitIds).then(async (result) => {
          setPreviewResult(result);

          // Seed group configs from server-provided configs
          const serverConfigs = result.groupConfigs || {};
          const configs = {};
          (result.results || []).forEach(r => {
            const sc = serverConfigs[r.groupId] || {};
            configs[r.groupId] = {
              targetItemTypeKey: sc.targetItemTypeKey || null,
              targetFitmentLocationKey: sc.targetFitmentLocationKey || null,
              targetAdditionalFitmentInfo: sc.targetAdditionalFitmentInfo || null,
              useExistingKitId: sc.useExistingKitId || null,
              kitDescription: sc.kitDescription || '',
              skuGroupKey: sc.skuGroupKey || null,
            };
          });

          // Auto-select exact match kits & initialize actions with conflict auto-skip
          const autoSelectedKits = {};
          (result.results || []).forEach(r => {
            const exactMatch = (r.similarKits || []).find(sk => sk.isExactMatch);
            if (exactMatch) autoSelectedKits[r.groupId] = exactMatch.kitId;
          });
          // Merge auto-selected kits into configs
          Object.entries(autoSelectedKits).forEach(([gid, kitId]) => {
            if (configs[gid] && !configs[gid].useExistingKitId) {
              configs[gid].useExistingKitId = kitId;
            }
          });
          setGroupConfigs(configs);

          // Load fitment location names for display in preview
          const itemTypeKeys = [...new Set(Object.values(configs).map(c => c.targetItemTypeKey).filter(Boolean))];
          for (const itk of itemTypeKeys) {
            try {
              const locs = await api.listFitmentLocationsForItemType(itk);
              setFitmentLocMap(prev => ({ ...prev, [itk]: locs }));
            } catch (_) { /* best effort */ }
          }

          const actions = {};
          (result.results || []).forEach(r => {
            const selectedKitId = configs[r.groupId]?.useExistingKitId || autoSelectedKits[r.groupId] || null;
            actions[r.groupId] = {
              defaultAction: 'skip',
              modelOverrides: buildConflictOverrides(r, selectedKitId),
              includeCrossMakeKeys: [],
              excludeModelKeys: []
            };
          });
          setGroupActions(actions);

          setStep('preview');
        }).catch(err => {
          setError(err?.response?.data?.error || err.message || 'Failed to load refresh preview');
        }).finally(() => {
          setBusy(false);
        });
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scroll to top when step changes
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [step]);

  // ── Load fitment locations when item type changes (returns locs for auto-select)
  const loadFitmentLocations = useCallback(async (itemTypeKey, groupId) => {
    if (!itemTypeKey) return [];
    if (fitmentLocMap[itemTypeKey]) {
      const cached = fitmentLocMap[itemTypeKey];
      // Auto-select if exactly one location
      if (groupId && cached.length === 1) {
        updateGroupConfig(groupId, { targetFitmentLocationKey: cached[0].FitmentLocationKey });
      }
      return cached;
    }
    try {
      const locs = await api.listFitmentLocationsForItemType(itemTypeKey);
      setFitmentLocMap(prev => ({ ...prev, [itemTypeKey]: locs }));
      // Auto-select if exactly one location
      if (groupId && locs.length === 1) {
        updateGroupConfig(groupId, { targetFitmentLocationKey: locs[0].FitmentLocationKey });
      }
      return locs;
    } catch { return []; }
  }, [fitmentLocMap, updateGroupConfig]);

  // ── Copy target config (ItemType/Location/AdditionalInfo) from one group to all others
  const copyTargetConfigToAll = useCallback((sourceGroupId) => {
    const source = groupConfigs[sourceGroupId];
    if (!source) return;
    setGroupConfigs(prev => {
      const next = { ...prev };
      for (const gid of Object.keys(next)) {
        if (gid === String(sourceGroupId)) continue;
        next[gid] = {
          ...next[gid],
          targetItemTypeKey: source.targetItemTypeKey,
          targetFitmentLocationKey: source.targetFitmentLocationKey,
          targetAdditionalFitmentInfo: source.targetAdditionalFitmentInfo
        };
      }
      return next;
    });
    // Ensure fitment locations are loaded for the copied item type
    if (source.targetItemTypeKey) loadFitmentLocations(source.targetItemTypeKey);
  }, [groupConfigs, loadFitmentLocations]);

  // ── File upload handler
  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setBusy(true);
    setFileName(file.name);
    try {
      const result = await api.parseImportFile(file);
      setParseResult(result);
      // Initialize group configs
      const configs = {};
      (result.kitGroups || []).forEach(g => {
        // Auto-resolve SKUGroupKey if the SKU already belongs to exactly one group,
        // or if the explicit SKUGroup name matched an existing group
        const si = g.skuInfo || {};
        let autoSkuGroupKey = null;
        let autoSkuResolved = false;
        if (si.existingSKUGroups?.length === 1) {
          autoSkuGroupKey = si.existingSKUGroups[0].SKUGroupKey;
          autoSkuResolved = true;
        } else if (si.matchedSKUGroup && si.skuGroup) {
          autoSkuGroupKey = si.matchedSKUGroup.SKUGroupKey;
          autoSkuResolved = true;
        }
        configs[g.groupId] = {
          kitDescription: '',
          targetItemTypeKey: null,
          targetFitmentLocationKey: null,
          targetAdditionalFitmentInfo: '',
          useExistingKitId: null,
          skuGroupKey: autoSkuGroupKey,
          skuResolved: autoSkuResolved
        };
      });
      setGroupConfigs(configs);
      if (result.kitGroups?.length && !result.errors?.length) {
        setStep('configure');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to parse file');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, []);

  // ── Run preview
  const runPreview = useCallback(async () => {
    if (!parseResult?.kitGroups?.length) return;
    setError('');
    setBusy(true);
    try {
      const groups = parseResult.kitGroups.map(g => {
        const config = groupConfigs[g.groupId] || {};
        return {
          groupId: g.groupId,
          make: g.make,
          components: g.components,
          targetItemTypeKey: config.targetItemTypeKey,
          targetFitmentLocationKey: config.targetFitmentLocationKey,
          targetAdditionalFitmentInfo: config.targetAdditionalFitmentInfo || null
        };
      });
      const result = await api.previewImport(groups);
      setPreviewResult(result);

      // Auto-select exact match kits & initialize actions with conflict auto-skip
      const autoSelectedKits = {}; // groupId → kitId
      (result.results || []).forEach(r => {
        const exactMatch = (r.similarKits || []).find(sk => sk.isExactMatch);
        if (exactMatch) autoSelectedKits[r.groupId] = exactMatch.kitId;
      });

      const actions = {};
      (result.results || []).forEach(r => {
        const selectedKitId = autoSelectedKits[r.groupId] || null;
        actions[r.groupId] = {
          defaultAction: 'skip',
          modelOverrides: buildConflictOverrides(r, selectedKitId),
          includeCrossMakeKeys: [],
          excludeModelKeys: []
        };
      });
      setGroupActions(actions);

      // Apply auto-selected kits to group configs + auto-generate descriptions
      setGroupConfigs(prev => {
        const next = { ...prev };
        (result.results || []).forEach(r => {
          const gid = r.groupId;
          const cfg = next[gid] || {};

          // Auto-select exact match kit if user hasn't manually picked one
          if (autoSelectedKits[gid] && !cfg.useExistingKitId) {
            next[gid] = { ...cfg, useExistingKitId: autoSelectedKits[gid] };
          }

          // Auto-generate kit description
          const updatedCfg = next[gid] || cfg;
          if (!userEditedDescription.current[gid] && !updatedCfg.kitDescription) {
            const autoDesc = buildDefaultKitDescription(updatedCfg, r.matchingModels, r.crossMakeModels, coreItemTypes, fitmentLocMap);
            if (autoDesc) {
              next[gid] = { ...updatedCfg, kitDescription: autoDesc };
            }
          }
        });
        return next;
      });

      setStep('preview');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Preview failed');
    } finally {
      setBusy(false);
    }
  }, [parseResult, groupConfigs, coreItemTypes, fitmentLocMap]);

  // ── Helper: determine if a result has zero actionable changes
  const isNoChangeResult = useCallback((result) => {
    if (!result) return true;
    // Kit OEM Mode: no changes if nothing new to link
    if (result.kitOemMode) {
      const overwrite = kitOemOverwrite[result.groupId] || false;
      const actionable = (result.linkableFitments || []).filter(f => {
        if (f.LinkedKitId === result.groupId) return false;
        if (f.LinkedKitId && !overwrite) return false;
        return true;
      });
      return actionable.length === 0;
    }
    // Regular mode
    const actions = groupActions[result.groupId] || {};
    const excludeSet = new Set(actions.excludeModelKeys || []);
    const crossMakeIncluded = new Set(actions.includeCrossMakeKeys || []);
    const conflictModelKeys = new Set((result.conflicts || []).map(c => c.ModelKey));
    // Check if any matching model will produce work
    const hasMatchingWork = (result.matchingModels || []).some(m => {
      if (excludeSet.has(m.ModelKey)) return false;
      if (!conflictModelKeys.has(m.ModelKey)) return true; // new create
      const override = actions.modelOverrides?.[String(m.ModelKey)];
      const action = override || actions.defaultAction || 'skip';
      return action !== 'skip';
    });
    if (hasMatchingWork) return false;
    // Check cross-make inclusions
    if (crossMakeIncluded.size > 0) return false;
    // Anything occupying the target position that ISN'T this kit still needs the user's
    // attention (a different kit number, or a part that doesn't belong there), so surface
    // the kit instead of collapsing it into the "no changes" list.
    const selectedKitId = groupConfigs[result.groupId]?.useExistingKitId || null;
    const matchingModelKeys = new Set((result.matchingModels || []).map(m => m.ModelKey));
    const hasMismatchedConflict = (result.conflicts || []).some(c => {
      if (!matchingModelKeys.has(c.ModelKey) || excludeSet.has(c.ModelKey)) return false;
      if (!selectedKitId) return true;
      return c.LinkedKitId !== selectedKitId && c.OEMNumber !== selectedKitId;
    });
    if (hasMismatchedConflict) return false;
    return true;
  }, [groupActions, groupConfigs, kitOemOverwrite]);

  // ── Execute import
  const runExecute = useCallback(async () => {
    if (!previewResult?.results?.length) return;
    setError('');
    setBusy(true);
    try {
      // Filter out kits with zero actionable changes
      const actionableResults = previewResult.results.filter(r => !isNoChangeResult(r));
      if (!actionableResults.length) {
        setExecuteResult({ results: [], summary: 'No kits had actionable changes.' });
        setStep('execute');
        setBusy(false);
        return;
      }
      const groups = actionableResults.map(r => {
        const config = groupConfigs[r.groupId] || {};
        const actions = groupActions[r.groupId] || {};

        // ── Kit OEM Mode: send fitment keys to link, not model keys
        if (r.kitOemMode) {
          const overwrite = kitOemOverwrite[r.groupId] || false;
          const linkableFitments = (r.linkableFitments || []).filter(f => {
            if (f.LinkedKitId === r.groupId) return false; // already linked to this kit
            if (f.LinkedKitId && !overwrite) return false; // linked to other kit, skip unless overwrite
            return true;
          });
          return {
            groupId: r.groupId,
            useExistingKitId: config.useExistingKitId || r.groupId,
            kitOemMode: true,
            linkFitmentKeys: linkableFitments.map(f => f.FitmentKey),
            components: r.components
          };
        }

        // ── Component Mode (existing behavior)
        const matchingModels = (r.matchingModels || []).map(m => m.ModelKey);
        const excludeSet = new Set(actions.excludeModelKeys || []);
        const filteredModels = matchingModels.filter(k => !excludeSet.has(k));

        return {
          groupId: r.groupId,
          useExistingKitId: config.useExistingKitId || null,
          kitDescription: config.kitDescription || null,
          components: r.components,
          targetItemTypeKey: config.targetItemTypeKey,
          targetFitmentLocationKey: config.targetFitmentLocationKey,
          targetAdditionalFitmentInfo: config.targetAdditionalFitmentInfo || null,
          skuGroupKey: config.skuGroupKey || null,
          modelKeys: filteredModels,
          defaultAction: actions.defaultAction || 'skip',
          modelOverrides: actions.modelOverrides || {},
          includeCrossMakeKeys: actions.includeCrossMakeKeys || []
        };
      });
      const result = await api.executeImport(groups);
      setExecuteResult(result);
      setStep('execute');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  }, [previewResult, groupConfigs, groupActions, kitOemOverwrite, isNoChangeResult]);

  // ── Update group action
  const updateGroupAction = useCallback((groupId, updates) => {
    setGroupActions(prev => ({
      ...prev,
      [groupId]: { ...prev[groupId], ...updates }
    }));
  }, []);

  const toggleCrossMakeModel = useCallback((groupId, modelKey) => {
    setGroupActions(prev => {
      const current = prev[groupId] || {};
      const keys = new Set(current.includeCrossMakeKeys || []);
      if (keys.has(modelKey)) keys.delete(modelKey); else keys.add(modelKey);
      return { ...prev, [groupId]: { ...current, includeCrossMakeKeys: [...keys] } };
    });
  }, []);

  const toggleExcludeModel = useCallback((groupId, modelKey) => {
    setGroupActions(prev => {
      const current = prev[groupId] || {};
      const keys = new Set(current.excludeModelKeys || []);
      if (keys.has(modelKey)) keys.delete(modelKey); else keys.add(modelKey);
      return { ...prev, [groupId]: { ...current, excludeModelKeys: [...keys] } };
    });
  }, []);

  const setModelOverride = useCallback((groupId, modelKey, action) => {
    setGroupActions(prev => {
      const current = prev[groupId] || {};
      const overrides = { ...(current.modelOverrides || {}) };
      if (action) overrides[String(modelKey)] = action;
      else delete overrides[String(modelKey)];
      return { ...prev, [groupId]: { ...current, modelOverrides: overrides } };
    });
  }, []);

  // ── Computed
  const stepIndex = STEPS.indexOf(step);
  const canContinue = useMemo(() => {
    if (step === 'upload') return !!parseResult?.kitGroups?.length;
    if (step === 'configure') {
      return parseResult?.kitGroups?.every(g => {
        const cfg = groupConfigs[g.groupId];
        return cfg?.targetItemTypeKey && cfg?.targetFitmentLocationKey;
      });
    }
    if (step === 'preview') return !!previewResult?.results?.length;
    return false;
  }, [step, parseResult, groupConfigs, previewResult]);

  if (!open) return null;

  // ─── Upload Step ──────────────────────────────────────────────────────────
  const renderUploadStep = () => (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem' }}>
          <button
            style={{ ...s.btn, ...s.btnSecondary }}
            onClick={() => api.downloadImportTemplate()}
          >
            Download Template
          </button>
          <span style={{ fontSize: '0.85rem', color: isDark ? '#999' : '#666' }}>
            CSV or XLSX — delete the instructions row before importing
          </span>
        </div>
        <div style={{ fontSize: '0.8rem', color: isDark ? '#999' : '#666', lineHeight: 1.6 }}>
          <strong>Required:</strong> GroupID, Make, OEMNumber &nbsp;|&nbsp;
          <strong>Optional:</strong> ItemType, FitmentLocation, AdditionalFitmentInfo, Quantity (default 1), SKU, SKUGroup
        </div>
      </div>

      <div
        style={{
          border: `2px dashed ${isDark ? '#555' : '#ccc'}`, borderRadius: '8px',
          padding: '2rem', textAlign: 'center', cursor: 'pointer',
          background: isDark ? '#252525' : '#f9fafb'
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls,.tsv,.txt"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        {busy ? (
          <div>Parsing file…</div>
        ) : fileName ? (
          <div>
            <strong>{fileName}</strong>
            <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: isDark ? '#999' : '#666' }}>
              Click to choose a different file
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📁</div>
            <div><strong>Click to select a file</strong></div>
            <div style={{ fontSize: '0.85rem', color: isDark ? '#999' : '#666', marginTop: '0.25rem' }}>
              Supports .csv, .xlsx, .xls, .tsv
            </div>
          </div>
        )}
      </div>

      {parseResult?.errors?.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <strong style={{ color: '#ef4444' }}>Errors ({parseResult.errors.length}):</strong>
          <div style={{ maxHeight: '150px', overflow: 'auto', marginTop: '0.3rem' }}>
            {parseResult.errors.map((e, i) => (
              <div key={i} style={s.error}>Row {e.row}: {e.message}</div>
            ))}
          </div>
        </div>
      )}

      {parseResult?.warnings?.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <strong style={{ color: '#f59e0b' }}>Warnings ({parseResult.warnings.length}):</strong>
          <div style={{ maxHeight: '150px', overflow: 'auto', marginTop: '0.3rem' }}>
            {parseResult.warnings.map((w, i) => (
              <div key={i} style={s.warning}>Row {w.row}: {w.message}</div>
            ))}
          </div>
        </div>
      )}

      {parseResult?.kitGroups?.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <strong style={s.success}>
            Parsed {parseResult.kitGroups.length} kit group{parseResult.kitGroups.length !== 1 ? 's' : ''} successfully
          </strong>
          <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {parseResult.kitGroups.map(g => (
              <span key={g.groupId} style={s.badge('#2563eb')}>
                Group {g.groupId}: {g.make} — {g.components.length} component{g.components.length !== 1 ? 's' : ''}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // ─── Configure Step ───────────────────────────────────────────────────────
  const renderConfigureStep = () => (
    <div>
      {parseResult?.kitGroups?.map(group => {
        const config = groupConfigs[group.groupId] || {};
        const locations = fitmentLocMap[config.targetItemTypeKey] || [];

        return (
          <div key={group.groupId} style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div>
                <strong style={{ fontSize: '1rem' }}>Kit Group {group.groupId}</strong>
                <span style={{ ...s.badge('#6366f1'), marginLeft: '0.5rem' }}>{group.make}</span>
                <span style={{ fontSize: '0.85rem', color: isDark ? '#999' : '#666', marginLeft: '0.5rem' }}>
                  {group.components.length} component{group.components.length !== 1 ? 's' : ''}
                </span>
              </div>
              {config.useExistingKitId && (
                <span style={s.badge('#22c55e')}>Using existing: {config.useExistingKitId}</span>
              )}
            </div>

            {/* Components table */}
            <table style={{ ...s.table, marginBottom: '0.75rem' }}>
              <thead>
                <tr>
                  <th style={s.th}>OEM Number</th>
                  <th style={s.th}>Item Type</th>
                  <th style={s.th}>Fitment Location</th>
                  <th style={s.th}>Additional Info</th>
                  <th style={s.th}>Qty</th>
                </tr>
              </thead>
              <tbody>
                {group.components.map((c, ci) => (
                  <tr key={ci}>
                    <td style={s.td}><strong>{c.oemNumber}</strong></td>
                    <td style={s.td}>{c.itemType || '—'}</td>
                    <td style={s.td}>{c.fitmentLocation || '—'}</td>
                    <td style={s.td}>{c.additionalFitmentInfo || '—'}</td>
                    <td style={s.td}>{c.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Target configuration */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <div>
                <label style={s.label}>Kit Description <span style={{ fontWeight: 400, fontSize: '0.75rem', color: isDark ? '#888' : '#999' }}>(auto-generated on preview)</span></label>
                <input
                  style={{ ...s.input, width: '100%' }}
                  placeholder="e.g. Throttle Cable Complete Kit"
                  value={config.kitDescription || ''}
                  onChange={e => {
                    userEditedDescription.current[group.groupId] = true;
                    updateGroupConfig(group.groupId, { kitDescription: e.target.value });
                  }}
                />
              </div>
              <div>
                <label style={s.label}>Target Item Type *</label>
                <SearchableSelect
                  options={coreItemTypes}
                  value={config.targetItemTypeKey}
                  labelKey="ItemType"
                  valueKey="ItemTypeKey"
                  placeholder="— Type to search Item Types —"
                  isDark={isDark}
                  onChange={val => {
                    updateGroupConfig(group.groupId, {
                      targetItemTypeKey: val,
                      targetFitmentLocationKey: null
                    });
                    if (val) loadFitmentLocations(val, group.groupId);
                  }}
                />
              </div>
              <div>
                <label style={s.label}>Target Fitment Location *</label>
                <SearchableSelect
                  options={locations}
                  value={config.targetFitmentLocationKey}
                  labelKey="FitmentLocation"
                  valueKey="FitmentLocationKey"
                  placeholder={config.targetItemTypeKey ? '— Type to search Locations —' : '— Select Item Type first —'}
                  disabled={!config.targetItemTypeKey}
                  isDark={isDark}
                  onChange={val => {
                    updateGroupConfig(group.groupId, { targetFitmentLocationKey: val });
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Target Additional Fitment Info</label>
                  <input
                    style={{ ...s.input, width: '100%' }}
                    placeholder="Optional"
                    value={config.targetAdditionalFitmentInfo || ''}
                    onChange={e => updateGroupConfig(group.groupId, { targetAdditionalFitmentInfo: e.target.value })}
                  />
                </div>
                {parseResult?.kitGroups?.length > 1 && (
                  <button
                    style={{ ...s.btn, ...s.btnSecondary, fontSize: '0.75rem', padding: '0.35rem 0.6rem', whiteSpace: 'nowrap', marginBottom: '1px' }}
                    title="Copy Item Type, Fitment Location & Additional Info to all other kit groups"
                    onClick={() => copyTargetConfigToAll(group.groupId)}
                  >
                    ⬇ Copy to All
                  </button>
                )}
              </div>
            </div>

            {/* ── SKU / SKU Group Section ── */}
            {(() => {
              const si = group.skuInfo || {};
              const hasSku = !!si.sku;
              const hasExplicitGroup = !!si.skuGroup;
              const suggestedName = si.suggestedGroupName || '';
              const existingGroups = si.existingSKUGroups || [];
              const matchedGroup = si.matchedSKUGroup;
              const matchedGroupSKUs = si.matchedSKUGroupSKUs || [];
              const resolved = config.skuResolved;
              const isBusy = skuBusy[group.groupId];
              const cgn = customGroupName[group.groupId] || suggestedName || '';

              if (!hasSku && !hasExplicitGroup) return null;

              return (
                <div style={{ borderTop: `1px solid ${isDark ? '#444' : '#e2e6ea'}`, paddingTop: '0.75rem', marginTop: '0.5rem' }}>
                  <strong style={{ fontSize: '0.9rem', marginBottom: '0.4rem', display: 'block' }}>
                    SKU / SKU Group
                  </strong>

                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                    {hasSku && (
                      <div>
                        <span style={{ color: isDark ? '#999' : '#666' }}>SKU:</span>{' '}
                        <strong>{si.sku}</strong>
                        {si.existingSKUKey
                          ? <span style={s.badge('#22c55e')}>Exists</span>
                          : <span style={s.badge('#f59e0b')}>New</span>
                        }
                      </div>
                    )}
                    {hasExplicitGroup && (
                      <div>
                        <span style={{ color: isDark ? '#999' : '#666' }}>SKU Group (from file):</span>{' '}
                        <strong>{si.skuGroup}</strong>
                      </div>
                    )}
                    {!hasExplicitGroup && suggestedName && (
                      <div>
                        <span style={{ color: isDark ? '#999' : '#666' }}>Suggested Group:</span>{' '}
                        <strong>{suggestedName}</strong>
                        <span style={{ color: isDark ? '#888' : '#999', fontSize: '0.8rem' }}> (derived from SKU)</span>
                      </div>
                    )}
                  </div>

                  {/* If already resolved, show badge */}
                  {resolved && config.skuGroupKey ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={s.badge('#22c55e')}>SKU Group Assigned (Key: {config.skuGroupKey})</span>
                      <button
                        style={{ ...s.btn, ...s.btnSecondary, fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                        onClick={() => updateGroupConfig(group.groupId, { skuGroupKey: null, skuResolved: false })}
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <div>
                      {/* Show existing groups for this SKU */}
                      {existingGroups.length > 0 && (
                        <div style={{ marginBottom: '0.5rem' }}>
                          <div style={{ fontSize: '0.8rem', color: isDark ? '#999' : '#666', marginBottom: '0.3rem' }}>
                            This SKU already belongs to {existingGroups.length} group{existingGroups.length !== 1 ? 's' : ''}:
                          </div>
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            {existingGroups.map(eg => (
                              <button
                                key={eg.SKUGroupKey}
                                style={{ ...s.btn, ...s.btnPrimary, fontSize: '0.8rem', padding: '0.25rem 0.6rem' }}
                                disabled={isBusy}
                                onClick={() => updateGroupConfig(group.groupId, { skuGroupKey: eg.SKUGroupKey, skuResolved: true })}
                              >
                                Use "{eg.SKUGroup}" (Key: {eg.SKUGroupKey})
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Show matched group (from explicit name or suggested) */}
                      {matchedGroup && !existingGroups.some(eg => eg.SKUGroupKey === matchedGroup.SKUGroupKey) && (
                        <div style={{ marginBottom: '0.5rem' }}>
                          <div style={{ fontSize: '0.8rem', color: isDark ? '#999' : '#666', marginBottom: '0.3rem' }}>
                            Found existing group "{matchedGroup.SKUGroup}":
                            {matchedGroupSKUs.length > 0 && (
                              <span> ({matchedGroupSKUs.length} SKU{matchedGroupSKUs.length !== 1 ? 's' : ''}: {matchedGroupSKUs.map(s2 => s2.SKU).join(', ')})</span>
                            )}
                          </div>
                          <button
                            style={{ ...s.btn, ...s.btnPrimary, fontSize: '0.8rem', padding: '0.25rem 0.6rem' }}
                            disabled={isBusy}
                            onClick={async () => {
                              setSkuBusy(prev => ({ ...prev, [group.groupId]: true }));
                              try {
                                const result = await api.resolveSkuForImport({
                                  sku: si.sku || null,
                                  addToGroupKey: matchedGroup.SKUGroupKey,
                                  createSku: !si.existingSKUKey
                                });
                                updateGroupConfig(group.groupId, { skuGroupKey: result.skuGroupKey, skuResolved: true });
                              } catch (err) {
                                setError(err?.response?.data?.error || err.message || 'Failed to resolve SKU');
                              } finally {
                                setSkuBusy(prev => ({ ...prev, [group.groupId]: false }));
                              }
                            }}
                          >
                            {isBusy ? 'Working…' : `Add to "${matchedGroup.SKUGroup}" & Use`}
                          </button>
                        </div>
                      )}

                      {/* Create new group option */}
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div>
                          <label style={{ ...s.label, fontSize: '0.8rem' }}>New Group Name</label>
                          <input
                            style={{ ...s.input, width: '180px' }}
                            value={cgn}
                            onChange={e => setCustomGroupName(prev => ({ ...prev, [group.groupId]: e.target.value }))}
                            placeholder="e.g. 011111"
                          />
                        </div>
                        <button
                          style={{ ...s.btn, ...s.btnPrimary, fontSize: '0.8rem', padding: '0.35rem 0.7rem' }}
                          disabled={isBusy || !cgn.trim()}
                          onClick={async () => {
                            setSkuBusy(prev => ({ ...prev, [group.groupId]: true }));
                            try {
                              const result = await api.resolveSkuForImport({
                                sku: si.sku || null,
                                skuGroupName: cgn.trim(),
                                createGroup: true,
                                createSku: !si.existingSKUKey
                              });
                              updateGroupConfig(group.groupId, { skuGroupKey: result.skuGroupKey, skuResolved: true });
                            } catch (err) {
                              setError(err?.response?.data?.error || err.message || 'Failed to create SKU group');
                            } finally {
                              setSkuBusy(prev => ({ ...prev, [group.groupId]: false }));
                            }
                          }}
                        >
                          {isBusy ? 'Creating…' : 'Create Group & Use'}
                        </button>
                        <button
                          style={{ ...s.btn, ...s.btnSecondary, fontSize: '0.8rem', padding: '0.35rem 0.7rem' }}
                          onClick={() => updateGroupConfig(group.groupId, { skuGroupKey: null, skuResolved: true })}
                        >
                          Skip (No SKU Group)
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );

  // ─── Preview Step ─────────────────────────────────────────────────────────
  const renderPreviewStep = () => {
    const allResults = previewResult?.results || [];
    const noChangeResults = allResults.filter(r => isNoChangeResult(r));
    const changedResults = allResults.filter(r => !isNoChangeResult(r));

    return (
    <div>
      {busy && isRefreshMode && !previewResult && (
        <div style={{ padding: '2rem', textAlign: 'center', color: isDark ? '#999' : '#666' }}>
          Loading refresh preview…
        </div>
      )}
      {/* Show skipped kits warning in refresh mode */}
      {isRefreshMode && previewResult?.missing?.length > 0 && (
        <div style={{ ...s.warning, marginBottom: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: '6px' }}>
          <strong>{previewResult.missing.length} kit(s) skipped</strong> (missing required fields):
          {previewResult.missing.map(m => (
            <div key={m.kitId} style={{ fontSize: '0.85rem', marginTop: '0.2rem' }}>
              <code>{m.kitId}</code> — {m.reason}{m.description ? ` (${m.description})` : ''}
            </div>
          ))}
        </div>
      )}
      {/* Collapsible "No Changes" section */}
      {noChangeResults.length > 0 && (
        <div style={{
          marginBottom: '0.75rem', borderRadius: '8px',
          border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
          background: isDark ? '#1f2937' : '#f9fafb',
          overflow: 'hidden'
        }}>
          <button
            type="button"
            onClick={() => setNoChangeSectionOpen(prev => !prev)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.6rem 1rem', border: 'none', cursor: 'pointer',
              background: isDark ? '#1f2937' : '#f9fafb',
              color: isDark ? '#9ca3af' : '#6b7280', fontSize: '0.9rem', fontWeight: 600
            }}
          >
            <span>
              {noChangeSectionOpen ? '▾' : '▸'}{' '}
              {noChangeResults.length} kit{noChangeResults.length !== 1 ? 's' : ''} with no changes
            </span>
            <span style={{ fontSize: '0.8rem', fontWeight: 400 }}>
              {noChangeSectionOpen ? 'Click to hide' : 'Click to show'}
            </span>
          </button>
          {noChangeSectionOpen && (
            <div style={{ padding: '0.5rem 1rem 0.75rem' }}>
              {noChangeResults.map(result => {
                const config = groupConfigs[result.groupId] || {};
                const targetItemType = coreItemTypes.find(t => t.ItemTypeKey === config.targetItemTypeKey);
                const locations = fitmentLocMap[config.targetItemTypeKey] || [];
                const targetLocation = locations.find(l => l.FitmentLocationKey === config.targetFitmentLocationKey);
                return (
                  <div key={result.groupId} style={{
                    padding: '0.4rem 0.5rem', fontSize: '0.85rem',
                    borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
                    display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap'
                  }}>
                    <strong>{isRefreshMode ? result.groupId : `Kit Group ${result.groupId}`}</strong>
                    <span style={s.badge('#6366f1')}>{result.make}</span>
                    <span style={{ color: isDark ? '#999' : '#666' }}>
                      {targetItemType?.ItemType || '?'} / {targetLocation?.FitmentLocation || '?'}
                    </span>
                    {config.kitDescription && (
                      <span style={{ color: isDark ? '#888' : '#777', fontStyle: 'italic' }}>
                        — {config.kitDescription}
                      </span>
                    )}
                    <span style={{ ...s.badge(isDark ? '#374151' : '#d1d5db'), color: isDark ? '#9ca3af' : '#6b7280' }}>
                      No changes
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {changedResults.map(result => {
        const config = groupConfigs[result.groupId] || {};
        const actions = groupActions[result.groupId] || {};
        const excludeSet = new Set(actions.excludeModelKeys || []);
        const crossMakeIncluded = new Set(actions.includeCrossMakeKeys || []);
        const conflictModelKeys = new Set((result.conflicts || []).map(c => c.ModelKey));
        const targetItemType = coreItemTypes.find(t => t.ItemTypeKey === config.targetItemTypeKey);
        const locations = fitmentLocMap[config.targetItemTypeKey] || [];
        const targetLocation = locations.find(l => l.FitmentLocationKey === config.targetFitmentLocationKey);

        return (
          <div key={result.groupId} style={s.card}>
            <div style={{ marginBottom: '0.75rem' }}>
              <strong style={{ fontSize: '1rem' }}>{isRefreshMode ? result.groupId : `Kit Group ${result.groupId}`}</strong>
              <span style={{ ...s.badge('#6366f1'), marginLeft: '0.5rem' }}>{result.make}</span>
              <span style={{ fontSize: '0.85rem', color: isDark ? '#999' : '#666', marginLeft: '0.5rem' }}>
                → {targetItemType?.ItemType || '?'} / {targetLocation?.FitmentLocation || '?'}
                {config.targetAdditionalFitmentInfo ? ` / ${config.targetAdditionalFitmentInfo}` : ''}
              </span>
              {config.useExistingKitId && (
                <span style={{ ...s.badge('#22c55e'), marginLeft: '0.5rem' }}>
                  Using: {config.useExistingKitId}
                </span>
              )}
              {config.skuGroupKey && (
                <span style={{ ...s.badge('#8b5cf6'), marginLeft: '0.5rem' }}>
                  SKU Group: {config.skuGroup || config.skuGroupKey}
                </span>
              )}
            </div>

            {/* Editable kit description */}
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ ...s.label, fontSize: '0.8rem' }}>Kit Description</label>
              <input
                style={{ ...s.input, width: '100%' }}
                placeholder="Auto-generated — type to override"
                value={config.kitDescription || ''}
                onChange={e => {
                  userEditedDescription.current[result.groupId] = true;
                  updateGroupConfig(result.groupId, { kitDescription: e.target.value });
                }}
              />
            </div>

            {/* ── Kit OEM Mode: show linkable fitments ── */}
            {result.kitOemMode ? (
              <div>
                <div style={{
                  padding: '0.5rem 0.75rem', borderRadius: '6px', marginBottom: '0.75rem',
                  background: isDark ? '#1a2a3a' : '#eff6ff',
                  border: `1px solid ${isDark ? '#3b82f6' : '#93c5fd'}`,
                  fontSize: '0.85rem'
                }}>
                  <strong>Kit OEM Mode</strong> — This kit uses Product OEM Numbers to find existing fitments.
                  {Array.isArray(result.productOEMs) && result.productOEMs.length > 0 && (
                    <span style={{ marginLeft: '0.5rem' }}>
                      OEMs: {result.productOEMs.join(', ')}
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem', fontSize: '0.85rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ color: '#22c55e' }}>
                    ✓ Available: <strong>{result.availableCount || 0}</strong>
                  </span>
                  <span style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                    Already linked: <strong>{result.alreadyLinkedCount || 0}</strong>
                  </span>
                  {(result.linkedToOtherCount || 0) > 0 && (
                    <span style={{ color: '#f59e0b' }}>
                      Linked to other kit: <strong>{result.linkedToOtherCount}</strong>
                    </span>
                  )}
                  {(result.linkedToOtherCount || 0) > 0 && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', fontSize: '0.8rem' }}>
                      <input
                        type="checkbox"
                        checked={!!kitOemOverwrite[result.groupId]}
                        onChange={e => setKitOemOverwrite(prev => ({ ...prev, [result.groupId]: e.target.checked }))}
                      />
                      Also re-link fitments from other kits
                    </label>
                  )}
                </div>

                {result.linkableFitments?.length > 0 ? (
                  <div style={{ maxHeight: '350px', overflow: 'auto' }}>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={s.th}>Make</th>
                          <th style={s.th}>Model</th>
                          <th style={s.th}>Street Name</th>
                          <th style={s.th}>Year</th>
                          <th style={s.th}>OEM</th>
                          <th style={s.th}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.linkableFitments.map((f, fi) => {
                          const isLinkedToUs = f.LinkedKitId === result.groupId;
                          const isLinkedToOther = f.LinkedKitId && !isLinkedToUs;
                          const rowBg = isLinkedToUs
                            ? (isDark ? '#1a2a1a' : '#f0fdf4')
                            : isLinkedToOther
                              ? (isDark ? '#3a2a0a' : '#fffbeb')
                              : 'transparent';
                          return (
                            <tr key={fi} style={{ background: rowBg }}>
                              <td style={s.td}>{f.Make || '—'}</td>
                              <td style={s.td}>{f.ModelName || '—'}</td>
                              <td style={s.td}>{f.StreetName || '—'}</td>
                              <td style={s.td}>{f.Year != null ? String(Number(f.Year)) : '—'}</td>
                              <td style={s.td}>{f.OEMNumber || '—'}</td>
                              <td style={s.td}>
                                {isLinkedToUs ? (
                                  <span style={{ color: '#22c55e', fontSize: '0.8rem' }}>Already linked</span>
                                ) : isLinkedToOther ? (
                                  <span style={{ color: '#f59e0b', fontSize: '0.8rem' }}>
                                    Linked to {f.LinkedKitId}
                                  </span>
                                ) : (
                                  <span style={{ color: '#3b82f6', fontSize: '0.8rem' }}>Will link</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ ...s.warning, marginTop: '0.4rem' }}>
                    No existing fitments found matching the Product OEM numbers at this position.
                  </div>
                )}
              </div>
            ) : (
            <>
            {/* Similar existing kits */}
            {result.similarKits?.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <strong style={{ fontSize: '0.9rem' }}>Similar Existing Kits</strong>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                  {result.similarKits.map(sk => {
                    const isSelected = config.useExistingKitId === sk.kitId;
                    const extraKey = `${result.groupId}-${sk.kitId}`;
                    const showExtras = expandedExtraKits[extraKey];

                    const mismatchKey = `${result.groupId}-${sk.kitId}-mismatches`;
                    const showMismatches = expandedExtraKits[mismatchKey];

                    // Border/background coloring
                    let borderColor = isDark ? '#444' : '#ddd';
                    let bgColor = 'transparent';
                    if (isSelected) {
                      borderColor = '#22c55e';
                      bgColor = isDark ? '#1a3a1a' : '#f0fdf4';
                    } else if (sk.isExactMatch) {
                      borderColor = '#22c55e';
                    } else if (sk.hasSwappedAssignments) {
                      borderColor = '#3b82f6';
                      bgColor = isDark ? '#1a2a3a' : '#eff6ff';
                    } else if (sk.hasItemTypeMismatch) {
                      borderColor = '#f59e0b';
                      bgColor = isDark ? '#3a2a0a' : '#fffbeb';
                    }

                    return (
                      <div key={sk.kitId}>
                        <div
                          style={{
                            display: 'flex', alignItems: 'center', gap: '0.75rem',
                            padding: '0.5rem 0.75rem', borderRadius: '6px',
                            border: `1px solid ${borderColor}`,
                            background: bgColor
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div>
                              <strong>{sk.kitId}</strong> — {sk.description}
                              {sk.isExactMatch && (
                                <span style={{ ...s.badge('#22c55e'), marginLeft: '0.5rem' }}>✓ Exact Match</span>
                              )}
                              {sk.hasSwappedAssignments && (
                                <span style={{ ...s.badge('#3b82f6'), marginLeft: '0.5rem' }}>↔ Same Parts, Assignments Swapped</span>
                              )}
                              {sk.hasItemTypeMismatch && !sk.hasSwappedAssignments && (
                                <span style={{ ...s.badge('#f59e0b'), marginLeft: '0.5rem' }}>⚠ ItemType/Location Differs</span>
                              )}
                              {sk.hasQuantityMismatch && !sk.hasItemTypeMismatch && (
                                <span style={{ ...s.badge('#f59e0b'), marginLeft: '0.5rem' }}>⚠ Qty Differs</span>
                              )}
                            </div>
                            <div style={{ fontSize: '0.8rem', marginTop: '0.2rem' }}>
                              <span style={{ color: '#22c55e' }}>Match: {sk.matchPercent}%</span>
                              {' | '}
                              <span style={{ color: '#22c55e' }}>
                                Matching: {sk.matchingOEMs?.length || 0}
                              </span>
                              {sk.missingInKit?.length > 0 && (
                                <span style={{ color: '#ef4444' }}>
                                  {' | Missing in kit: '}
                                  {sk.missingInKit.join(', ')}
                                </span>
                              )}
                              {sk.hasExtraComponents && (
                                <span style={{ color: '#3b82f6' }}>
                                  {' | '}{sk.extraInKit.length} extra component{sk.extraInKit.length !== 1 ? 's' : ''} in kit{' '}
                                  <button
                                    style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: '0.8rem' }}
                                    onClick={() => setExpandedExtraKits(prev => ({ ...prev, [extraKey]: !prev[extraKey] }))}
                                  >
                                    {showExtras ? 'Hide' : 'View'}
                                  </button>
                                </span>
                              )}
                              {' | Usage: '}{sk.usageCount}
                              {sk.componentMismatches?.length > 0 && (
                                <span>
                                  {' | '}
                                  <button
                                    style={{ background: 'none', border: 'none', color: sk.hasSwappedAssignments ? '#3b82f6' : '#f59e0b', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: '0.8rem' }}
                                    onClick={() => setExpandedExtraKits(prev => ({ ...prev, [mismatchKey]: !prev[mismatchKey] }))}
                                  >
                                    {showMismatches ? 'Hide' : 'View'} {sk.componentMismatches.length} difference{sk.componentMismatches.length !== 1 ? 's' : ''}
                                  </button>
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            style={{
                              ...s.btn,
                              ...(isSelected ? s.btnDanger : s.btnPrimary)
                            }}
                            onClick={() => {
                              const newKitId = isSelected ? null : sk.kitId;
                              updateGroupConfig(result.groupId, { useExistingKitId: newKitId });
                              // When selecting a kit, auto-skip conflicts that already use this kit
                              if (newKitId) {
                                setGroupActions(prev => {
                                  const ga = { ...prev };
                                  const existing = ga[result.groupId] || {};
                                  const overrides = { ...(existing.modelOverrides || {}) };
                                  (result.conflicts || []).forEach(c => {
                                    if (c.LinkedKitId === newKitId) {
                                      overrides[String(c.ModelKey)] = 'skip';
                                    } else if (overrides[String(c.ModelKey)] === 'skip') {
                                      // Clear auto-skip if it was set for a different kit
                                      delete overrides[String(c.ModelKey)];
                                    }
                                  });
                                  ga[result.groupId] = { ...existing, modelOverrides: overrides };
                                  return ga;
                                });
                              }
                            }}
                          >
                            {isSelected ? 'Deselect' : 'Use This Kit'}
                          </button>
                        </div>

                        {/* Expandable extra components */}
                        {showExtras && sk.extraComponents?.length > 0 && (
                          <div style={{
                            margin: '0.25rem 0 0 1rem', padding: '0.5rem 0.75rem',
                            borderRadius: '0 0 6px 6px',
                            border: `1px solid ${isDark ? '#444' : '#ddd'}`, borderTop: 'none',
                            background: isDark ? '#1e1e1e' : '#f9fafb', fontSize: '0.8rem'
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Extra Components in Kit:</div>
                            <table style={{ ...s.table, fontSize: '0.8rem' }}>
                              <thead>
                                <tr>
                                  <th style={s.th}>OEM Number</th>
                                  <th style={s.th}>Qty</th>
                                  <th style={s.th}>SKU</th>
                                  <th style={s.th}>Note</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sk.extraComponents.map((ec, idx) => (
                                  <tr key={idx}>
                                    <td style={s.td}>{ec.ComponentOEMNumber}</td>
                                    <td style={s.td}>{ec.Quantity}</td>
                                    <td style={s.td}>{ec.SKU || '—'}</td>
                                    <td style={s.td}>{ec.OEMNote || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Expandable component mismatch details */}
                        {showMismatches && sk.componentMismatches?.length > 0 && (
                          <div style={{
                            margin: '0.25rem 0 0 1rem', padding: '0.5rem 0.75rem',
                            borderRadius: '0 0 6px 6px',
                            border: `1px solid ${sk.hasSwappedAssignments ? '#3b82f6' : '#f59e0b'}`,
                            borderTop: 'none',
                            background: isDark ? '#1e1e1e' : (sk.hasSwappedAssignments ? '#eff6ff' : '#fffbeb'),
                            fontSize: '0.8rem'
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>
                              {sk.hasSwappedAssignments ? 'Assignment Differences (same parts, different positions):' : 'Component Differences:'}
                            </div>
                            <table style={{ ...s.table, fontSize: '0.8rem' }}>
                              <thead>
                                <tr>
                                  <th style={s.th}>OEM</th>
                                  <th style={s.th}>Field</th>
                                  <th style={s.th}>Import Value</th>
                                  <th style={s.th}>Kit Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sk.componentMismatches.flatMap((cm, idx) => {
                                  const rows = [];
                                  if (cm.qtyImport != null || cm.qtyKit != null) {
                                    rows.push(
                                      <tr key={`${idx}-qty`}>
                                        <td style={s.td}>{cm.oem}</td>
                                        <td style={s.td}>Qty</td>
                                        <td style={s.td}>{cm.qtyImport ?? '—'}</td>
                                        <td style={s.td}>{cm.qtyKit ?? '—'}</td>
                                      </tr>
                                    );
                                  }
                                  if (cm.itemTypeNameImport || cm.itemTypeNameKit) {
                                    rows.push(
                                      <tr key={`${idx}-it`}>
                                        <td style={s.td}>{cm.oem}</td>
                                        <td style={s.td}>ItemType</td>
                                        <td style={s.td}>{cm.itemTypeNameImport || '—'}</td>
                                        <td style={s.td}>{cm.itemTypeNameKit || '—'}</td>
                                      </tr>
                                    );
                                  }
                                  if (cm.fitmentLocationNameImport || cm.fitmentLocationNameKit) {
                                    rows.push(
                                      <tr key={`${idx}-fl`}>
                                        <td style={s.td}>{cm.oem}</td>
                                        <td style={s.td}>Location</td>
                                        <td style={s.td}>{cm.fitmentLocationNameImport || '—'}</td>
                                        <td style={s.td}>{cm.fitmentLocationNameKit || '—'}</td>
                                      </tr>
                                    );
                                  }
                                  return rows;
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Matching models (same make) */}
            <div style={{ marginBottom: '0.75rem' }}>
              <strong style={{ fontSize: '0.9rem' }}>
                Matching Models — {result.make} ({(result.matchingModels || []).length})
              </strong>
              {result.matchingModels?.length > 0 ? (
                <div style={{ maxHeight: '250px', overflow: 'auto', marginTop: '0.4rem' }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>
                          <input
                            type="checkbox"
                            checked={result.matchingModels.every(m => !excludeSet.has(m.ModelKey))}
                            onChange={(e) => {
                              if (e.target.checked) {
                                updateGroupAction(result.groupId, { excludeModelKeys: [] });
                              } else {
                                updateGroupAction(result.groupId, {
                                  excludeModelKeys: result.matchingModels.map(m => m.ModelKey)
                                });
                              }
                            }}
                          />
                        </th>
                        <th style={s.th}>Model</th>
                        <th style={s.th}>Street Name</th>
                        <th style={s.th}>Year</th>
                        <th style={s.th}>Region</th>
                        <th style={s.th}>Conflict?</th>
                        <th style={s.th}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.matchingModels.map(m => {
                        const hasConflict = conflictModelKeys.has(m.ModelKey);
                        const override = actions.modelOverrides?.[String(m.ModelKey)];
                        const excluded = excludeSet.has(m.ModelKey);
                        return (
                          <tr key={m.ModelKey} style={{ opacity: excluded ? 0.4 : 1 }}>
                            <td style={s.td}>
                              <input
                                type="checkbox"
                                checked={!excluded}
                                onChange={() => toggleExcludeModel(result.groupId, m.ModelKey)}
                              />
                            </td>
                            <td style={s.td}>{m.ModelName}</td>
                            <td style={s.td}>{m.StreetName || '—'}</td>
                            <td style={s.td}>{m.Year != null ? String(Number(m.Year)) : '—'}</td>
                            <td style={s.td}>{m.Region || '—'}</td>
                            <td style={s.td}>
                              {hasConflict ? (
                                <span style={s.badge('#f59e0b')}>Conflict</span>
                              ) : (
                                <span style={{ color: '#22c55e', fontSize: '0.8rem' }}>Clear</span>
                              )}
                            </td>
                            <td style={s.td}>
                              {hasConflict ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  <select
                                    style={{ ...s.select, fontSize: '0.8rem' }}
                                    value={override || ''}
                                    onChange={e => setModelOverride(result.groupId, m.ModelKey, e.target.value || null)}
                                  >
                                    <option value="">Use default</option>
                                    {CONFLICT_ACTIONS.map(a => (
                                      <option key={a.value} value={a.value}>{a.label}</option>
                                    ))}
                                  </select>
                                  {override === 'skip' && config.useExistingKitId && (() => {
                                    const conflict = result.conflicts?.find(c => String(c.ModelKey) === String(m.ModelKey));
                                    const isLinked = conflict?.LinkedKitId === config.useExistingKitId;
                                    const oemMatchesKit = conflict?.OEMNumber && conflict.OEMNumber === config.useExistingKitId;
                                    return (isLinked || oemMatchesKit)
                                      ? <span style={{ fontSize: '0.75rem', color: '#22c55e' }}>(already linked)</span>
                                      : null;
                                  })()}
                                </div>
                              ) : (
                                <span style={{ fontSize: '0.8rem', color: isDark ? '#999' : '#666' }}>Create</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ ...s.warning, marginTop: '0.4rem' }}>
                  No matching models found for {result.make} with all components.
                </div>
              )}
            </div>

            {/* Cross-make models */}
            {result.crossMakeModels?.length > 0 && (
              <div style={{ marginBottom: '0.75rem' }}>
                <strong style={{ fontSize: '0.9rem', color: '#f59e0b' }}>
                  Cross-Make Matches ({result.crossMakeModels.length})
                </strong>
                <div style={{ fontSize: '0.8rem', color: isDark ? '#999' : '#666', marginBottom: '0.3rem' }}>
                  These models from other makes share the same OEM numbers. Select any you want to include.
                </div>
                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Include</th>
                        <th style={s.th}>Make</th>
                        <th style={s.th}>Model</th>
                        <th style={s.th}>Street Name</th>
                        <th style={s.th}>Year</th>
                        <th style={s.th}>Region</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.crossMakeModels.map(m => (
                        <tr key={m.ModelKey}>
                          <td style={s.td}>
                            <input
                              type="checkbox"
                              checked={crossMakeIncluded.has(m.ModelKey)}
                              onChange={() => toggleCrossMakeModel(result.groupId, m.ModelKey)}
                            />
                          </td>
                          <td style={s.td}>
                            <span style={s.badge('#f59e0b')}>{m.Make}</span>
                          </td>
                          <td style={s.td}>{m.ModelName}</td>
                          <td style={s.td}>{m.StreetName || '—'}</td>
                          <td style={s.td}>{m.Year != null ? String(Number(m.Year)) : '—'}</td>
                          <td style={s.td}>{m.Region || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Conflicts detail */}
            {result.conflicts?.length > 0 && (
              <div style={{ marginBottom: '0.75rem' }}>
                <strong style={{ fontSize: '0.9rem', color: '#ef4444' }}>
                  Existing Fitments at Target Position ({result.conflicts.length})
                </strong>
                <div style={{ marginBottom: '0.3rem' }}>
                  <label style={{ fontSize: '0.85rem', marginRight: '0.5rem' }}>Default action for conflicts:</label>
                  <select
                    style={s.select}
                    value={actions.defaultAction || 'skip'}
                    onChange={e => updateGroupAction(result.groupId, { defaultAction: e.target.value })}
                  >
                    {CONFLICT_ACTIONS.map(a => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </select>
                </div>
                <div style={{ maxHeight: '200px', overflow: 'auto', marginTop: '0.3rem' }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Model</th>
                        <th style={s.th}>Existing OEM</th>
                        <th style={s.th}>Location</th>
                        <th style={s.th}>Additional Info</th>
                        <th style={s.th}>Is Kit?</th>
                        <th style={s.th}>Linked Kit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.conflicts.map((c, ci) => {
                        const model = [...(result.matchingModels || []), ...(result.crossMakeModels || [])]
                          .find(m => m.ModelKey === c.ModelKey);
                        const oemMatchesKit = c.OEMNumber && c.OEMNumber === config.useExistingKitId;
                        const linkedToThisKit = !!config.useExistingKitId && c.LinkedKitId === config.useExistingKitId;
                        const mismatched = !oemMatchesKit && !linkedToThisKit;
                        return (
                          <tr key={ci} style={mismatched ? { background: isDark ? '#3a2a0a' : '#fffbeb' } : undefined}>
                            <td style={s.td}>{model?.ModelName || `Model #${c.ModelKey}`}</td>
                            <td style={s.td}>
                              {c.OEMNumber || '—'}
                              {mismatched && (
                                <span
                                  style={{ ...s.badge('#f59e0b'), marginLeft: '0.4rem' }}
                                  title="This row does not hold the kit number being applied"
                                >
                                  ≠ kit
                                </span>
                              )}
                            </td>
                            <td style={s.td}>{c.FitmentLocation || '—'}</td>
                            <td style={s.td}>{c.AdditionalFitmentInfo || '—'}</td>
                            <td style={s.td}>
                              {c.IsKit ? 'Yes' : oemMatchesKit
                                ? <span title="OEM matches kit but IsKit flag not set — will be repaired on execute" style={{ color: '#f59e0b' }}>No*</span>
                                : 'No'}
                            </td>
                            <td style={s.td}>{c.LinkedKitId || (oemMatchesKit ? <span title="Fitment has kit OEM but no FitmentKits entry — will be repaired on execute" style={{ color: '#f59e0b' }}>—*</span> : '—')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            </>
            )}
          </div>
        );
      })}
    </div>
    );
  };

  // ─── Execute Step ─────────────────────────────────────────────────────────
  const renderExecuteStep = () => (
    <div>
      {busy ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Importing…</div>
          <div style={{ color: isDark ? '#999' : '#666' }}>Please wait while kits are created and fitments are assigned.</div>
        </div>
      ) : executeResult ? (
        <div>
          <h3 style={{ margin: '0 0 1rem' }}>Import Complete</h3>
          {executeResult.summary && !executeResult.results?.length && (
            <div style={{ padding: '1rem', textAlign: 'center', color: isDark ? '#999' : '#666', fontSize: '0.95rem' }}>
              {executeResult.summary}
            </div>
          )}
          {(executeResult.results || []).map(r => (
            <div key={r.groupId} style={s.card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <strong>Group {r.groupId}</strong>
                {r.success ? (
                  <span style={s.badge('#22c55e')}>Success</span>
                ) : (
                  <span style={s.badge('#ef4444')}>Failed</span>
                )}
                {r.kitId && <span style={{ fontSize: '0.85rem' }}>Kit: <strong>{r.kitId}</strong></span>}
                {r.kitOemMode && <span style={{ fontSize: '0.75rem', color: '#3b82f6' }}>Kit OEM Mode</span>}
              </div>
              {r.success ? (
                r.kitOemMode ? (
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.9rem' }}>
                    <div><strong>{r.linked}</strong> fitments linked</div>
                    {r.failed > 0 && <div style={{ color: '#ef4444' }}><strong>{r.failed}</strong> failed</div>}
                    <div style={{ color: isDark ? '#999' : '#666' }}>{r.totalFitments} total fitments</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.9rem' }}>
                    <div><strong>{r.created}</strong> fitments created</div>
                    <div><strong>{r.overwritten}</strong> overwritten</div>
                    <div><strong>{r.additionalCreated}</strong> additional rows</div>
                    {r.repaired > 0 && <div><strong>{r.repaired}</strong> repaired</div>}
                    <div><strong>{r.skipped}</strong> skipped</div>
                    <div style={{ color: isDark ? '#999' : '#666' }}>{r.totalModels} total models</div>
                  </div>
                )
              ) : (
                <div style={s.error}>{r.error}</div>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );

  // ─── Main Render ──────────────────────────────────────────────────────────
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={s.header}>
          <div>
            <h3 style={{ margin: 0 }}>{isRefreshMode ? 'Refresh Fitments' : 'Kit Importer'}</h3>
            {/* Stepper */}
            <div style={{ ...s.stepper, marginTop: '0.5rem' }}>
              {STEPS.filter(st => isRefreshMode ? (st === 'preview' || st === 'execute') : true).map((st, i, arr) => {
                const currentIdx = arr.indexOf(step);
                return (
                <React.Fragment key={st}>
                  {i > 0 && <span style={{ color: isDark ? '#555' : '#ccc' }}>—</span>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <div style={s.stepDot(step === st, currentIdx > i)}>
                      {currentIdx > i ? '✓' : i + 1}
                    </div>
                    <span style={{
                      fontWeight: step === st ? 600 : 400,
                      color: step === st ? (isDark ? '#e5e5e5' : '#222') : (isDark ? '#999' : '#666')
                    }}>
                      {STEP_LABELS[st]}
                    </span>
                  </div>
                </React.Fragment>
                );
              })}
            </div>
          </div>
          <button style={{ ...s.btn, ...s.btnSecondary }} onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div ref={bodyRef} style={s.body}>
          {error && <div style={{ ...s.error, marginBottom: '0.75rem', padding: '0.5rem', background: isDark ? '#3b1111' : '#fef2f2', borderRadius: '6px' }}>{error}</div>}
          {step === 'upload' && renderUploadStep()}
          {step === 'configure' && renderConfigureStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'execute' && renderExecuteStep()}
        </div>

        {/* Footer */}
        <div style={s.footer}>
          <div>
            {stepIndex > 0 && step !== 'execute' && !(isRefreshMode && step === 'preview') && (
              <button
                style={{ ...s.btn, ...s.btnSecondary }}
                disabled={busy}
                onClick={() => setStep(STEPS[stepIndex - 1])}
              >
                ← Back
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {step === 'upload' && canContinue && (
              <button
                style={{ ...s.btn, ...s.btnPrimary }}
                onClick={() => setStep('configure')}
              >
                Continue →
              </button>
            )}
            {step === 'configure' && (
              <button
                style={{ ...s.btn, ...s.btnPrimary }}
                disabled={!canContinue || busy}
                onClick={runPreview}
              >
                {busy ? 'Loading…' : 'Find Matching Models →'}
              </button>
            )}
            {step === 'preview' && (
              <button
                style={{ ...s.btn, ...s.btnPrimary }}
                disabled={busy}
                onClick={runExecute}
              >
                {busy ? 'Importing…' : 'Import Kits'}
              </button>
            )}
            {step === 'execute' && !busy && (
              <button
                style={{ ...s.btn, ...s.btnPrimary }}
                onClick={() => {
                  if (onImportComplete) onImportComplete();
                  onClose();
                }}
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
